import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import { Alert, Modal, Skeleton } from '@/components/ui';
import type { TimeEntryTask } from '@/types';

/**
 * Underopgaverne paa én OnePro-opgave.
 *
 * Ligger her og ikke i den ene side, fordi begge steder viser den samme liste: /timer bag
 * Handlinger-knappen, og Timer-fanen paa /kontrakter/:id bag den samme knap. To kopier ville
 * betyde to steder at rette en tom tilstand eller en fejlbesked.
 *
 * Komponenten monteres foerst naar en raekke er valgt, saa forespoergslen ikke skal betinges -
 * og listen hentes forfra hver gang et nyt vindue aabnes.
 */
export function SubtaskModal({
  todoId,
  title,
  subtitle,
  onClose,
}: {
  todoId: string;
  title: string | null;
  /** Kontekst under overskriften - fx "Board - kategori · Kunde". Valgfri. */
  subtitle?: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['time-entry-tasks', todoId],
    queryFn: () => api.get<{ data: TimeEntryTask[] }>(`/time-entries/${todoId}/tasks`),
  });

  const tasks = data?.data ?? [];

  return (
    <Modal open onClose={onClose} size="lg" title={title ?? 'Underopgaver'} description={subtitle}>
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-8" />
          ))}
        </div>
      ) : error ? (
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'Kunne ikke hente underopgaver.'}
        </Alert>
      ) : tasks.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-muted">
          Der er ingen underopgaver på denne opgave.
        </p>
      ) : (
        <ol className="divide-y divide-line text-sm">
          {tasks.map((task, index) => (
            <li key={task.id} className="flex gap-3 py-2">
              <span className="w-5 shrink-0 text-right text-xs text-ink-muted tabular">
                {index + 1}
              </span>
              <span className="min-w-0">{task.name ?? '—'}</span>
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}
